// /app/frontend/src/pages/public/BreachIntakeFormPage.jsx
// Public Breach Intake Form - Staff reporter (No authentication required)
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { breachIntakeAPI, tempGuestAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import {
  AlertCircle, Send, Loader2, Check,
  ChevronRight, ChevronLeft
} from 'lucide-react';
import debounce from 'lodash/debounce';

// Data types for breach (plain language)
const DATA_TYPES = [
  'Names & contact info',
  'Financial info',
  'Health info',
  'ID documents',
  'Passwords/credentials',
  'Not sure',
  'Other'
];

// Number affected ranges
const AFFECTED_RANGES = [
  '1-10',
  '11-50',
  '51-100',
  '101-500',
  '501-1,000',
  '1,001-10,000',
  'More than 10,000',
  'Not sure'
];

// Who affected options
const WHO_AFFECTED = [
  'Customers',
  'Staff/Employees',
  'Both',
  'Not sure'
];

const STEPS = [
  { id: 1, title: 'What Happened?' },
  { id: 2, title: 'About the Data' },
  { id: 3, title: 'About You' },
];

export default function BreachIntakeFormPage({ guestMode = false }) {
  const { token } = useParams();
  const navigate = useNavigate();
  const guestUser = guestMode ? JSON.parse(localStorage.getItem('guest_user') || '{}') : null;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [lastSaved, setLastSaved] = useState(null);
  const [touched, setTouched] = useState({});

  const [formData, setFormData] = useState({
    // Step 1: What Happened?
    awareness_datetime: '',
    awareness_time: '09:00',
    incident_datetime: '',
    incident_time: '09:00',
    what_happened: '',
    system_involved: '',

    // Step 2: About the Data
    data_types: [],
    number_affected_range: '',
    who_affected: [],
    actions_taken: '',
    anyone_else_told: 'no',
    anyone_else_told_details: '',

    // Step 3: About You
    staff_name: '',
    staff_department: '',
    staff_email: '',
  });

  // Fetch intake data
  useEffect(() => {
    const fetchIntake = async () => {
      try {
        setLoading(true);
        let response;
        if (guestMode) {
          response = await tempGuestAPI.getBreach();
          const fd = response.data.form_data || {};
          setFormData(prev => ({
            ...prev,
            staff_name: guestUser?.full_name || '',
            staff_email: guestUser?.email || '',
            ...fd,
          }));
          if (response.data.last_saved_at) setLastSaved(new Date(response.data.last_saved_at));
        } else {
          response = await breachIntakeAPI.getPublic(token);
          if (response.data.form_data && Object.keys(response.data.form_data).length > 0) {
            setFormData(prev => ({ ...prev, ...response.data.form_data }));
          }
        }
      } catch (err) {
        console.error('Failed to load intake:', err);
        if (err.response?.status === 410) {
          setError('expired');
        } else if (err.response?.status === 400) {
          setError('completed');
        } else if (guestMode) {
          // Non-fatal in guest mode — just pre-fill name/email and continue
          if (guestUser) {
            setFormData(prev => ({
              ...prev,
              staff_name: guestUser.full_name || '',
              staff_email: guestUser.email || '',
            }));
          }
        } else {
          setError('not_found');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchIntake();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // guestMode is a prop fixed at mount — capture in ref so the debounce closure
  // never reads a stale value regardless of when it fires.
  const guestModeRef = React.useRef(guestMode);
  const tokenRef = React.useRef(token);
  useEffect(() => { tokenRef.current = token; }, [token]);

  // Auto-save debounced — created once at mount, reads from refs at call time.
  const debouncedSave = useCallback(
    debounce(async (data) => {
      try {
        setSaving(true);
        if (guestModeRef.current) {
          await tempGuestAPI.saveBreach({ form_data: data });
        } else {
          await breachIntakeAPI.saveProgress(tokenRef.current, { form_data: data });
        }
        setLastSaved(new Date());
      } catch (err) {
        console.error('Auto-save failed:', err);
      } finally {
        setSaving(false);
      }
    }, 2000),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Update field and trigger auto-save
  const updateField = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      debouncedSave(updated);
      return updated;
    });
  };

  // Toggle checkbox in array
  const toggleCheckbox = (field, value) => {
    setFormData(prev => {
      const array = prev[field] || [];
      const updated = array.includes(value)
        ? array.filter(item => item !== value)
        : [...array, value];
      const newState = { ...prev, [field]: updated };
      debouncedSave(newState);
      return newState;
    });
  };

  // Mark field as touched
  const markFieldTouched = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  // Validation helper functions
  const isStepValid = (step) => {
    switch (step) {
      case 1:
        return formData.awareness_datetime && formData.what_happened;
      case 2:
        return formData.data_types.length > 0 && formData.number_affected_range && formData.who_affected.length > 0;
      case 3:
        return formData.staff_name && formData.staff_department && formData.staff_email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.staff_email);
      default:
        return true;
    }
  };

  const canNavigateToStep = (targetStep) => {
    // Can always go back
    if (targetStep < currentStep) return true;
    // Can only go forward if current step is valid
    return isStepValid(currentStep);
  };

  const getFieldError = (field) => {
    if (!touched[field]) return null;

    switch (field) {
      case 'awareness_datetime':
        return !formData.awareness_datetime ? 'Please enter when you noticed the problem' : null;
      case 'what_happened':
        return !formData.what_happened ? 'Please describe what happened' : null;
      case 'data_types':
        return formData.data_types.length === 0 ? 'Please select at least one data type' : null;
      case 'number_affected_range':
        return !formData.number_affected_range ? 'Please specify how many people are affected' : null;
      case 'who_affected':
        return formData.who_affected.length === 0 ? 'Please select who was affected' : null;
      case 'staff_name':
        return !formData.staff_name ? 'Please enter your name' : null;
      case 'staff_department':
        return !formData.staff_department ? 'Please enter your department' : null;
      case 'staff_email':
        if (!formData.staff_email) return 'Please enter your email address';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.staff_email)) return 'Please enter a valid email address';
        return null;
      default:
        return null;
    }
  };

  // Handle submit
  const handleSubmit = async () => {
    try {
      // Validate required fields
      if (!formData.awareness_datetime) {
        toast.error('Please enter when you noticed the problem');
        return;
      }
      if (!formData.what_happened) {
        toast.error('Please describe what happened');
        return;
      }
      if (formData.data_types.length === 0) {
        toast.error('Please select what type of information was involved');
        return;
      }
      if (!formData.number_affected_range) {
        toast.error('Please specify how many people are affected');
        return;
      }
      if (!formData.who_affected.length) {
        toast.error('Please specify who was affected');
        return;
      }
      if (!formData.staff_name) {
        toast.error('Please enter your name');
        return;
      }
      if (!formData.staff_department) {
        toast.error('Please enter your department');
        return;
      }
      if (!formData.staff_email) {
        toast.error('Please enter your email address');
        return;
      }

      // Combine date and time fields
      const submissionData = {
        ...formData,
        awareness_datetime: formData.awareness_datetime && formData.awareness_time
          ? `${formData.awareness_datetime}T${formData.awareness_time}:00Z`
          : formData.awareness_datetime,
        incident_datetime: formData.incident_datetime && formData.incident_time
          ? `${formData.incident_datetime}T${formData.incident_time}:00Z`
          : null,
      };
      delete submissionData.awareness_time;
      delete submissionData.incident_time;

      setSubmitting(true);
      if (guestMode) {
        await tempGuestAPI.submitBreach({ form_data: submissionData, title: submissionData.system_involved || 'Breach Report' });
        navigate('/guest/portal');
      } else {
        const response = await breachIntakeAPI.submit(token, { form_data: submissionData });
        sessionStorage.setItem('breach_reference', response.data.breach_reference);
        sessionStorage.setItem('submitted_at', response.data.submitted_at);
        navigate(`/breach-intake/${token}/success`);
      }
    } catch (err) {
      console.error('Submission failed:', err);
      if (err.response?.status === 410) {
        toast.error('This link has expired');
      } else if (err.response?.status === 400) {
        toast.error('This form has already been submitted');
      } else {
        toast.error('Failed to submit form. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Render step 1: What happened?
  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">When did you notice the problem? *</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="awareness_date" className="block mb-2 text-sm font-medium">Date *</Label>
            <Input
              id="awareness_date"
              type="date"
              value={formData.awareness_datetime}
              onChange={(e) => updateField('awareness_datetime', e.target.value)}
              onBlur={() => markFieldTouched('awareness_datetime')}
              className={`w-full ${getFieldError('awareness_datetime') ? 'border-red-500' : ''}`}
            />
            {getFieldError('awareness_datetime') && (
              <p className="text-red-500 text-xs mt-1">{getFieldError('awareness_datetime')}</p>
            )}
          </div>
          <div>
            <Label htmlFor="awareness_time" className="block mb-2 text-sm font-medium">Time</Label>
            <Input
              id="awareness_time"
              type="time"
              value={formData.awareness_time}
              onChange={(e) => updateField('awareness_time', e.target.value)}
              className="w-full"
            />
          </div>
        </div>
      </div>

      <Separator />

      <div>
        <h3 className="text-lg font-semibold mb-4">When did it happen? <span className="text-xs font-normal text-gray-500">(optional)</span></h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="incident_date" className="block mb-2 text-sm font-medium">Date</Label>
            <Input
              id="incident_date"
              type="date"
              value={formData.incident_datetime}
              onChange={(e) => updateField('incident_datetime', e.target.value)}
              className="w-full"
            />
          </div>
          <div>
            <Label htmlFor="incident_time" className="block mb-2 text-sm font-medium">Time</Label>
            <Input
              id="incident_time"
              type="time"
              value={formData.incident_time}
              onChange={(e) => updateField('incident_time', e.target.value)}
              className="w-full"
            />
          </div>
        </div>
      </div>

      <Separator />

      <div>
        <h3 className="text-lg font-semibold mb-4">What happened? *</h3>
        <p className="text-sm text-gray-600 mb-3">Describe in your own words what you think happened.</p>
        <Textarea
          placeholder="Example: A laptop containing customer data was left on a train. The device was recovered but we cannot confirm if it was accessed..."
          value={formData.what_happened}
          onChange={(e) => updateField('what_happened', e.target.value)}
          onBlur={() => markFieldTouched('what_happened')}
          className={`w-full min-h-32 ${getFieldError('what_happened') ? 'border-red-500' : ''}`}
        />
        {getFieldError('what_happened') && (
          <p className="text-red-500 text-xs mt-1">{getFieldError('what_happened')}</p>
        )}
      </div>

      <Separator />

      <div>
        <h3 className="text-lg font-semibold mb-4">What system or file was involved? <span className="text-xs font-normal text-gray-500">(optional)</span></h3>
        <Input
          placeholder="Example: Customer CRM database, Finance spreadsheet, Email server..."
          value={formData.system_involved}
          onChange={(e) => updateField('system_involved', e.target.value)}
          className="w-full"
        />
      </div>
    </div>
  );

  // Render step 2: About the data
  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">What type of information was involved? *</h3>
        <div className="space-y-3">
          {DATA_TYPES.map(type => (
            <div key={type} className="flex items-center space-x-2">
              <Checkbox
                id={`data-type-${type}`}
                checked={formData.data_types.includes(type)}
                onCheckedChange={() => {
                  toggleCheckbox('data_types', type);
                  markFieldTouched('data_types');
                }}
              />
              <Label htmlFor={`data-type-${type}`} className="font-normal cursor-pointer">{type}</Label>
            </div>
          ))}
        </div>
        {getFieldError('data_types') && (
          <p className="text-red-500 text-xs mt-2">{getFieldError('data_types')}</p>
        )}
      </div>

      <Separator />

      <div>
        <h3 className="text-lg font-semibold mb-4">Roughly how many people are affected? *</h3>
        <div className="space-y-3">
          {AFFECTED_RANGES.map(range => (
            <div key={range} className="flex items-center space-x-2">
              <RadioGroup value={formData.number_affected_range} onValueChange={(value) => {
                updateField('number_affected_range', value);
                markFieldTouched('number_affected_range');
              }}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value={range} id={`range-${range}`} />
                  <Label htmlFor={`range-${range}`} className="font-normal cursor-pointer">{range}</Label>
                </div>
              </RadioGroup>
            </div>
          ))}
        </div>
        {getFieldError('number_affected_range') && (
          <p className="text-red-500 text-xs mt-2">{getFieldError('number_affected_range')}</p>
        )}
      </div>

      <Separator />

      <div>
        <h3 className="text-lg font-semibold mb-4">Who were the people affected? *</h3>
        <div className="space-y-3">
          {WHO_AFFECTED.map(who => (
            <div key={who} className="flex items-center space-x-2">
              <Checkbox
                id={`who-${who}`}
                checked={formData.who_affected.includes(who)}
                onCheckedChange={() => {
                  toggleCheckbox('who_affected', who);
                  markFieldTouched('who_affected');
                }}
              />
              <Label htmlFor={`who-${who}`} className="font-normal cursor-pointer">{who}</Label>
            </div>
          ))}
        </div>
        {getFieldError('who_affected') && (
          <p className="text-red-500 text-xs mt-2">{getFieldError('who_affected')}</p>
        )}
      </div>

      <Separator />

      <div>
        <h3 className="text-lg font-semibold mb-4">What steps have already been taken? <span className="text-xs font-normal text-gray-500">(optional)</span></h3>
        <Textarea
          placeholder="Example: Notified IT team, isolated the affected system, changed access passwords..."
          value={formData.actions_taken}
          onChange={(e) => updateField('actions_taken', e.target.value)}
          className="w-full min-h-32"
        />
      </div>

      <Separator />

      <div>
        <h3 className="text-lg font-semibold mb-4">Has anyone else been told about this?</h3>
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <RadioGroup value={formData.anyone_else_told} onValueChange={(value) => updateField('anyone_else_told', value)}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="not_told" />
                <Label htmlFor="not_told" className="font-normal cursor-pointer">No, no one else has been told</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="has_told" />
                <Label htmlFor="has_told" className="font-normal cursor-pointer">Yes, others have been told</Label>
              </div>
            </RadioGroup>
          </div>
        </div>

        {formData.anyone_else_told === 'yes' && (
          <div className="mt-4">
            <Label htmlFor="told_details" className="block mb-2 text-sm font-medium">Who and when?</Label>
            <Textarea
              id="told_details"
              placeholder="Example: Told the IT manager on March 24, told the Finance director on March 25..."
              value={formData.anyone_else_told_details}
              onChange={(e) => updateField('anyone_else_told_details', e.target.value)}
              className="w-full min-h-24"
            />
          </div>
        )}
      </div>
    </div>
  );

  // Render step 3: About you
  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          We need your contact details so the DPO can follow up with you about this report.
        </p>
      </div>

      <div>
        <Label htmlFor="name" className="block mb-2 text-sm font-medium">Your name *</Label>
        <Input
          id="name"
          placeholder="Full name"
          value={formData.staff_name}
          onChange={(e) => updateField('staff_name', e.target.value)}
          onBlur={() => markFieldTouched('staff_name')}
          className={`w-full ${getFieldError('staff_name') ? 'border-red-500' : ''}`}
        />
        {getFieldError('staff_name') && (
          <p className="text-red-500 text-xs mt-1">{getFieldError('staff_name')}</p>
        )}
      </div>

      <div>
        <Label htmlFor="department" className="block mb-2 text-sm font-medium">Your department *</Label>
        <Input
          id="department"
          placeholder="Example: HR, Finance, IT..."
          value={formData.staff_department}
          onChange={(e) => updateField('staff_department', e.target.value)}
          onBlur={() => markFieldTouched('staff_department')}
          className={`w-full ${getFieldError('staff_department') ? 'border-red-500' : ''}`}
        />
        {getFieldError('staff_department') && (
          <p className="text-red-500 text-xs mt-1">{getFieldError('staff_department')}</p>
        )}
      </div>

      <div>
        <Label htmlFor="email" className="block mb-2 text-sm font-medium">Your email address *</Label>
        <Input
          id="email"
          type="email"
          placeholder="your.email@company.com"
          value={formData.staff_email}
          onChange={(e) => updateField('staff_email', e.target.value)}
          onBlur={() => markFieldTouched('staff_email')}
          className={`w-full ${getFieldError('staff_email') ? 'border-red-500' : ''}`}
        />
        {getFieldError('staff_email') && (
          <p className="text-red-500 text-xs mt-1">{getFieldError('staff_email')}</p>
        )}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-sm text-amber-900">
          <strong>Your confirmation:</strong> I confirm the information above is accurate to the best of my knowledge.
        </p>
      </div>
    </div>
  );

  // Error states
  if (error === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Link Expired
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">
              This breach intake link has expired. Please ask your DPO to send you a new link.
            </p>
            <Button variant="outline" onClick={() => window.close()}>
              Close this page
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error === 'completed') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-600">
              <Check className="h-5 w-5" />
              Already Submitted
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">
              This intake form has already been submitted. Your DPO has received your report and will follow up shortly.
            </p>
            <Button variant="outline" onClick={() => window.close()}>
              Close this page
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error === 'not_found') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Link Not Found
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">
              The intake link is invalid or not found. Please check the URL and try again.
            </p>
            <Button variant="outline" onClick={() => window.close()}>
              Close this page
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <p className="text-gray-600">Loading form...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Gradient Header */}
      <div className="bg-gradient-to-r from-red-700 to-red-900 text-white py-8 px-6 border-b border-primary/20 shadow-sm">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <AlertCircle className="w-8 h-8" />
            <h1 className="text-3xl font-bold">Data Breach Report</h1>
          </div>
          <p className="text-red-100 text-sm">Please provide details about the incident. This form should take 5-10 minutes.</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Progress Steps */}
          <div className="mb-8 flex gap-2 items-center justify-center">
            {STEPS.map((step, idx) => (
              <React.Fragment key={step.id}>
                <button
                  onClick={() => {
                    if (canNavigateToStep(step.id)) {
                      setCurrentStep(step.id);
                    } else {
                      toast.error(`Please complete Step ${currentStep} before moving forward`);
                    }
                  }}
                  disabled={step.id > currentStep && !isStepValid(currentStep)}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium text-sm transition-all ${
                    currentStep === step.id
                      ? 'bg-red-600 text-white shadow-lg'
                      : currentStep > step.id
                      ? 'bg-green-500/20 text-green-700 dark:text-green-400 border border-green-500/30'
                      : step.id > currentStep && !isStepValid(currentStep)
                      ? 'bg-slate-100 dark:bg-slate-800 text-slate-400 border border-slate-300 dark:border-slate-600 cursor-not-allowed'
                      : 'bg-slate-200 dark:bg-slate-700 text-foreground border border-slate-300 dark:border-slate-600'
                  }`}
                >
                  <div className="text-xs font-semibold">Step {step.id}</div>
                  <div className="truncate">{step.title}</div>
                </button>
                {idx < STEPS.length - 1 && <div className="text-slate-400">→</div>}
              </React.Fragment>
            ))}
          </div>

          {/* Form Card */}
          <Card className="shadow-lg border border-primary/10 mb-6">
            <CardHeader className="bg-slate-50 dark:bg-slate-900 border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Step {currentStep}: {STEPS[currentStep - 1].title}</CardTitle>
                {lastSaved && (
                  <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-xs">
                    <Check className="h-4 w-4" />
                    Saved
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-8">
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3()}
            </CardContent>
          </Card>

          {/* Navigation Buttons */}
          <div className="flex gap-3 items-center">
            <Button
              onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
              disabled={currentStep === 1 || submitting}
              className="bg-slate-600 hover:bg-slate-700 text-white"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back
            </Button>

            {currentStep < STEPS.length ? (
              <Button
                onClick={() => {
                  if (isStepValid(currentStep)) {
                    setCurrentStep(currentStep + 1);
                  } else {
                    toast.error(`Please complete all required fields on Step ${currentStep}`);
                  }
                }}
                disabled={submitting || !isStepValid(currentStep)}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={submitting || saving}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit Report
                  </>
                )}
              </Button>
            )}
          </div>

          {saving && (
            <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
              Auto-saving...
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
